import React from "react";
//#region types
import { EntityPaginationProps } from "models/propTypes/navigationPropTypes";
//#endregion
//#region components
import { Grid, Typography, CircularProgress } from "@mui/material";
import { WChevronLeft, WChevronRight } from "utils/constants/iconDefinitions";
import RoundButton from "../../buttons/roundButton";
import Icon from "../../icon";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { CSSHelper } from "utils/helpers";
import clsx from "clsx";

const EntityPagination: React.FC<EntityPaginationProps> = (props) => {
  const {
    total,
    numPerPage,
    initialPage,
    initialIndex,
    onNavigationNext,
    onBeforePageEnd,
    loadingNext,
    preserveIndexOnPageNumberChange,
    navigationDelay,
  } = props;
  const [currentIndex, setCurrentIndex] = React.useState<number>();
  const [isBorderIndex, setIsBorderIndex] = React.useState<{ isFirst: boolean }>();

  const updateCurrentIndex = () => {
    const calculatedIndex =
      initialPage && numPerPage ? initialPage * numPerPage + (initialIndex ?? 0) : initialIndex ?? 0;
    setCurrentIndex(calculatedIndex);
  };

  React.useEffect(() => {
    updateCurrentIndex();
  }, [initialIndex, initialPage]);

  React.useEffect(() => {
    if (!preserveIndexOnPageNumberChange) {
      updateCurrentIndex();
    }
  }, [numPerPage]);

  React.useEffect(() => {
    let newIndex = currentIndex;
    if (newIndex !== undefined) {
      let page = 0;
      let pageIndex = newIndex;

      if (total && newIndex >= total) {
        newIndex = total - 1;
      }

      if (numPerPage) {
        page = Math.floor(newIndex / numPerPage);
        pageIndex = newIndex % numPerPage;
      }

      props.onCurrentIndexUpdate(pageIndex, page);

      if (onBeforePageEnd && total && numPerPage) {
        const lastPage = Math.ceil(total / numPerPage) - 1;
        if (pageIndex === 0) {
          onBeforePageEnd!(page > 0 ? page - 1 : lastPage);
          setIsBorderIndex({ isFirst: true });
        } else if (pageIndex === numPerPage - 1 || newIndex === total - 1) {
          onBeforePageEnd!(page < lastPage ? page + 1 : 0);
          setIsBorderIndex({ isFirst: false });
        } else {
          setIsBorderIndex(undefined);
        }
      }
    }
  }, [currentIndex]);

  React.useEffect(() => {
    if (total && currentIndex !== undefined && total <= currentIndex) {
      setCurrentIndex(total - 1);
    }
  }, [total]);

  const itemNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    props.onBeforeNavigation && props.onBeforeNavigation();

    const navigate = () => {
      if (total && currentIndex !== undefined) {
        let newIndex = onNavigationNext ? onNavigationNext() : undefined;
        if (newIndex === undefined)
          if (currentIndex < total - 1) {
            newIndex = currentIndex + 1;
          } else {
            newIndex = 0;
          }

        setCurrentIndex(newIndex);
      }
    };

    if (navigationDelay) {
      setTimeout(() => {
        navigate();
      }, navigationDelay);
    } else {
      navigate();
    }
  };

  const itemPrev = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    props.onBeforeNavigation && props.onBeforeNavigation();

    const navigate = () => {
      if (total && currentIndex !== undefined) {
        let newIndex;
        if (currentIndex > 0) {
          newIndex = currentIndex - 1;
        } else {
          newIndex = total - 1;
        }

        setCurrentIndex(newIndex);
      }
    };

    if (navigationDelay) {
      setTimeout(() => {
        navigate();
      }, navigationDelay);
    } else {
      navigate();
    }
  };

  return (
    <Grid item className={clsx(cssExports.entityNavigationContainer, props.className)}>
      <Grid item container className={cssExports.entityNavigation}>
        {total ? (
          <Grid
            item
            className={clsx(cssExports.entityNavigationPagingIcons, CSSHelper.getClassByDisplayType(props.displayType))}
            visibility={total === 1 ? "hidden" : undefined}
          >
            {loadingNext && isBorderIndex?.isFirst ? (
              <CircularProgress className={cssExports.progress} />
            ) : (
              <RoundButton size="xs" displayType={props.displayType} onClick={itemPrev} disableRipple>
                <Icon icon={WChevronLeft} />
              </RoundButton>
            )}
          </Grid>
        ) : (
          <></>
        )}
        <Grid item className={cssExports.entityNavigationPaging}>
          <Grid container wrap="nowrap" direction="row" columnSpacing={1}>
            {props.navigationText ? (
              <Grid item>
                <Typography>{props.navigationText}</Typography>
              </Grid>
            ) : (
              <></>
            )}
            <Grid item>
              <Typography>
                {total === undefined ? (
                  <CircularProgress className={cssExports.progress} />
                ) : total ? (
                  `${(currentIndex ?? 0) + 1} / ${total}`
                ) : (
                  0
                )}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {total ? (
          <Grid
            item
            className={clsx(cssExports.entityNavigationPagingIcons, CSSHelper.getClassByDisplayType(props.displayType))}
            visibility={total === 1 ? "hidden" : undefined}
          >
            {loadingNext && isBorderIndex?.isFirst === false ? (
              <CircularProgress className={cssExports.progress} />
            ) : (
              <RoundButton
                size="xs"
                displayType={props.displayType}
                onClick={itemNext}
                disableRipple
                disabled={loadingNext}
              >
                <Icon icon={WChevronRight} />
              </RoundButton>
            )}
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </Grid>
  );
};

export default EntityPagination;
